// Хелпер ошибок портала. Источник истины: `contract.md` §3.3, `pattern_error_translation.mdc`.
//
// `PortalApiError` — единый класс ошибок BFF-слоя: route handler ловит их и любой
// не-`PortalApiError` throw нормализует в обезличенный `INTERNAL_ERROR` (SEC-001,
// `contract.md` §3.1 робастный catch). `errorStatus(code)` — канонический маппинг
// код → HTTP (`pattern_error_translation.mdc` «Канонические коды ошибок»).
//
// Публичная поверхность модуля — строго `PortalApiError` + `errorStatus` (§3.3); другие
// потребители (`portalFetch`, route handler) инлайнят `new PortalApiError(...)` по контракту.

/** Канонический маппинг `pattern_error_translation.mdc`: code → HTTP-семантика. */
const ERROR_STATUS_MAP: Record<string, number> = {
  UNAUTHENTICATED: 401,
  INVALID_CREDENTIALS: 401,
  PERMISSION_DENIED: 403,
  NOT_FOUND: 404,
  NEWS_NOT_FOUND: 404, // add-personalized-news-feed (контракт §0.2)
  POST_NOT_FOUND: 404, // add-post-feed (доменные ошибки, контракт §H «Доменные ошибки»)
  PERSONA_NOT_PUBLISHED: 404, // add-persona-card (домен persona, контракт §A.1.4/§B.2.3)
  INVALID_CURRENT_PASSWORD: 400, // add-credentials-management (контракт §0.1, ARCH-CRED-D-006)
  RESET_KEY_INVALID: 400, // add-credentials-management (контракт §0.1, ARCH-CRED-D-006)
  VALIDATION_ERROR: 400,
  CONFLICT: 409,
  NEWS_FAVORITE_REMOVAL_BLOCKED: 409, // add-personalized-news-feed (контракт §0.2)
  RATE_LIMITED: 429,
  COUNTERPARTY_TERMS_REQUIRED: 403, // add-counterparty-check (контракт §0.2)
  COUNTERPARTY_NOT_FOUND: 404, // add-counterparty-check (контракт §0.2)
  COUNTERPARTY_RATE_LIMITED: 429, // add-counterparty-check (контракт §0.2; + Retry-After)
  COUNTERPARTY_SERVICE_UNAVAILABLE: 503, // add-counterparty-check (контракт §0.2)
  // client-communications (контракт §A.0, TD-013): 5 новых домен-кодов.
  // PROTECTED_CONNECTION/LAST_PATH → 409 (конфликт бизнес-инварианта, права есть —
  // владелец проверен JWT; CM §12.4 parity). NOT_OWNER → 403 (anti-enum message == NOT_FOUND).
  // VERIFICATION_LINK_INVALID/UNSUBSCRIBE_LINK_INVALID → 400 (public-link flows).
  NOT_OWNER: 403, // client-communications (контракт §A.0; anti-enum message == NOT_FOUND)
  PROTECTED_CONNECTION: 409, // client-communications (регистрационная неудаляема/неизменяема)
  LAST_PATH: 409, // client-communications (последний путь Helpdesk/CRM)
  VERIFICATION_LINK_INVALID: 400, // client-communications (public verify_email link)
  UNSUBSCRIBE_LINK_INVALID: 400, // client-communications (public unsubscribe link)
  // add-telegram-account-binding (контракт §A.0 / §B.5): 5 домен-кодов привязки.
  // Без регистрации здесь BFF схлопнул бы их в INTERNAL_ERROR (TD-013) и Mini App
  // потеряло бы различие «ссылка устарела» / «занят другим кабинетом».
  TELEGRAM_NOT_CONFIGURED: 503,
  TELEGRAM_IDENTITY_INVALID: 400,
  TELEGRAM_HANDSHAKE_INVALID: 400,
  TELEGRAM_ALREADY_BOUND: 409,
  TELEGRAM_BINDING_IN_PROGRESS: 409,
  // add-event-registration (контракт §0.2 MUST)
  EVENT_NOT_FOUND: 404,
  REGISTRATION_NOT_FOUND: 404,
  REGISTRATION_UNAVAILABLE: 409,
  CANCELLATION_CLOSED: 409,
  // add-messenger-signals (контракт §B.4): без регистрации BFF схлопнул бы
  // коды в 500 и Mini App потеряла бы различие «не связан» / «не найдено».
  MESSENGER_NOT_CONNECTED: 409,
  MESSENGER_OBJECT_NOT_FOUND: 404,
  INTERNAL_ERROR: 500,
};

/**
 * Единая ошибка портального API. Переносится в HTTP-ответ route handler'ом
 * по контракту `{success:false, error:{code, message, fields?}}` (§3.1).
 *
 * `fields` — опциональная карта field→сообщение (для `VALIDATION_ERROR`).
 *
 * `extras` — опциональные сквозные метаданные ошибки (AUTH-CONT-D-001, add-portal-auth):
 * пробрасываются из Frappe-`error` через `portalFetch._pickExtras`, не теряя
 * не-`code`/`message`/`fields` ключи (сейчас — `retry_after` для RATE_LIMITED,
 * контрактом §8.0/§5.6 route handler ставит HTTP 429 + заголовок `Retry-After`
 * из `extras.retry_after`; forward-compat для будущих доменов). Не нарушает
 * SEC-001/ARCH-D-015 — `extras` несут обезличенные значения, заданные контрактом.
 */
export class PortalApiError extends Error {
  readonly code: string;
  readonly status: number;
  readonly message: string;
  readonly fields?: Record<string, string>;
  readonly extras?: Record<string, unknown>;

  constructor(
    code: string,
    status: number,
    message: string,
    fields?: Record<string, string>,
    extras?: Record<string, unknown>,
  ) {
    super(message);
    this.name = 'PortalApiError';
    this.code = code;
    this.status = status;
    this.message = message;
    this.fields = fields;
    this.extras = extras;
  }
}

/**
 * Канонический маппинг кода ошибки в HTTP-статус (`pattern_error_translation.mdc`).
 *
 * Неизвестный код — аномалия: логируется серверно и клиенту отдаётся обезличенный
 * `INTERNAL_ERROR` (HTTP 500) — per `contract.md` §3.3 / `pattern_portal_bff.mdc`.
 * Для welcome-page неприменимо (единственный код — `INTERNAL_ERROR`, §2.5).
 */
export function errorStatus(code: string): number {
  const status = ERROR_STATUS_MAP[code];
  if (status !== undefined) {
    return status;
  }
  // Серверное логирование (best-effort): обезличивание ответа от него не зависит.
  // eslint-disable-next-line no-console
  console.error(
    `[portal/api] unknown error code "${code}" — normalizing to INTERNAL_ERROR (500)`,
  );
  return ERROR_STATUS_MAP.INTERNAL_ERROR;
}
